import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../lib/cn';

export const TooltipProvider = TooltipPrimitive.Provider;

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>['side'];
  /** Keyboard hint rendered after the label, e.g. "N". */
  shortcut?: string;
  delayDuration?: number;
}

/**
 * Tooltips are supplementary only.
 *
 * A tooltip is unavailable to touch users and is not a reliable accessible name,
 * so anything essential must also exist as an `aria-label` or visible text.
 * IconButton already requires its own label for exactly this reason.
 */
export function Tooltip({
  content,
  children,
  side = 'top',
  shortcut,
  delayDuration = 400,
}: TooltipProps) {
  return (
    <TooltipPrimitive.Root delayDuration={delayDuration}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            'z-[--z-tooltip] flex items-center gap-2 rounded-sm px-2 py-1',
            'bg-surface-inverse text-xs text-text-inverse shadow-e2',
            'data-[state=delayed-open]:animate-fade-in',
          )}
        >
          {content}
          {shortcut ? (
            <kbd className="rounded-sm border border-current/30 px-1 text-2xs opacity-80">
              {shortcut}
            </kbd>
          ) : null}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
